<template>
  <van-field
    class="publish-field"
    :class="[
      `publish-field--${variant}`,
      {
        'publish-field--readonly': isReadonly && readonlyBackground,
        'publish-field--disabled': isDisabled,
      },
    ]"
    v-bind="$attrs"
    :value="value"
    v-on="$listeners"
  >
    <template v-if="$slots.label" #label>
      <slot name="label" />
    </template>
    <template v-if="$slots.input" #input>
      <slot name="input" />
    </template>
    <template v-if="$slots['left-icon']" #left-icon>
      <slot name="left-icon" />
    </template>
    <template v-if="$slots['right-icon']" #right-icon>
      <slot name="right-icon" />
    </template>
    <template v-if="$slots.button" #button>
      <slot name="button" />
    </template>
    <template v-if="$slots.extra" #extra>
      <slot name="extra" />
    </template>
  </van-field>
</template>

<script>
export default {
  name: "PublishField",
  inheritAttrs: false,
  props: {
    value: {
      type: [String, Number, Array, Object, Boolean],
      default: "",
    },
    variant: {
      type: String,
      default: "default",
      validator: (value) =>
        ["default", "remark", "remark-compact", "summary"].includes(
          value,
      ),
    },
    readonlyBackground: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    // Boolean attributes declared without a value arrive as an empty string
    // in $attrs (for example, <PublishField readonly />). Treat those as
    // enabled while preserving explicit :readonly="false" / :disabled="false".
    isReadonly() {
      return this.isEnabledAttr(this.$attrs.readonly);
    },
    isDisabled() {
      return this.isEnabledAttr(this.$attrs.disabled);
    },
  },
  methods: {
    isEnabledAttr(value) {
      return value !== undefined && value !== null && value !== false;
    },
  },
};
</script>

<style lang="less" scoped>
@import "./tokens.less";

.publish-field {
  position: relative;
  display: block;
  min-height: auto;
  box-sizing: border-box;
  padding: 10px @publish-space-card-x 12px;
  color: @publish-text-primary;
  font-size: 14px;
  line-height: 22px;
  background: transparent;

  &::after {
    display: none;
  }

  /deep/ .van-field__label {
    display: block;
    width: 100%;
    margin: 0 0 7px;
    color: @publish-text-regular;
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    text-align: left !important;
    word-break: normal;
    overflow-wrap: normal;
  }

  /deep/ .van-field__label::after {
    display: none !important;
    content: none;
  }

  /deep/ .van-field__value {
    width: 100%;
  }

  /deep/ .van-field__body {
    min-height: @publish-control-height;
    box-sizing: border-box;
    padding: 8px 10px;
    background: @publish-surface;
    border: 1px solid @publish-border;
    border-radius: @publish-radius-control;
  }

  /deep/ .van-field__body:focus-within {
    border-color: @publish-brand;
  }

  /deep/ .van-field__control {
    color: @publish-text-primary;
    font-size: 14px;
    line-height: 22px;
  }

  /deep/ .van-field__control::placeholder {
    color: @publish-placeholder;
  }

  /deep/ .van-field__error-message {
    padding: 4px 2px 0;
    font-size: 12px;
    line-height: 18px;
  }

  &.van-cell--required::before {
    top: 10px;
    left: @publish-space-card-x;
    height: 20px;
    font-size: 14px;
    line-height: 20px;
  }

  &.van-cell--required /deep/ .van-field__label {
    box-sizing: border-box;
    padding-left: 10px;
  }

  &.van-field--disabled /deep/ .van-field__label,
  &.publish-field--disabled /deep/ .van-field__label,
  /deep/ .van-field__control:disabled {
    color: @publish-text-secondary;
    -webkit-text-fill-color: @publish-text-secondary;
  }
}

.publish-field.choice-field {
  /deep/ .van-field__body {
    padding: 0;
    background: transparent;
    border: 0;
  }

  /deep/ .van-field__body:focus-within {
    border-color: transparent;
  }
}

.publish-field.exclusive-phone-field {
  position: relative;
  z-index: 1;
  padding: 0;
  background: transparent;

  /deep/ .van-field__label {
    width: 100%;
    margin: 0 0 6px;
    padding: 0;
    color: @publish-text-regular;
    font-weight: 400;
    line-height: 20px;
  }

  /deep/ .van-field__label::after {
    display: none !important;
    content: none !important;
  }

  /deep/ .van-field__body {
    min-height: 40px;
    padding: 8px 10px;
    background: @publish-surface;
    border: 1px solid @publish-border;
    border-radius: 6px;
  }
}

.publish-field.category-attribute-field {
  padding: 0;
  background: transparent;

  &::after {
    display: none;
  }

  /deep/ .van-field__label {
    width: 100%;
    margin: 0 0 8px;
    padding: 0;
    color: #303846;
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
  }

  &.van-cell--required::before {
    top: 0;
    left: 0;
    height: 20px;
    line-height: 20px;
  }

  &.van-cell--required /deep/ .van-field__label {
    padding-left: 10px;
  }

  /deep/ .van-field__body {
    min-height: 40px;
    box-sizing: border-box;
    padding: 8px 10px;
    background: @publish-surface;
    border: 1px solid @publish-border;
    border-radius: 6px;
  }

  /deep/ .van-field__control {
    color: @publish-text-primary;
    font-size: 14px;
    line-height: 22px;
  }

  /deep/ .van-field__control::placeholder {
    color: @publish-placeholder;
  }
}

.publish-field.option-field {
  display: block;
  -webkit-tap-highlight-color: transparent;

  /deep/ .van-field__label {
    display: block;
    width: 100%;
    margin: 0 0 8px;
  }

  /deep/ .van-field__value {
    width: 100%;
  }

  /deep/ .van-field__body {
    min-height: 0;
    padding: 0;
    background: transparent;
    border: 0;
    border-radius: 0;
  }

  /deep/ .van-field__body:focus-within,
  /deep/ .van-field__body:active,
  &.van-cell:active {
    background: transparent !important;
    border: 0 !important;
    box-shadow: none !important;
  }

  /deep/ .van-field__value,
  /deep/ .van-field__control {
    background: transparent;
  }
}

.publish-field.dimension-field {
  min-width: 0;
  padding: 0;
  background: transparent;

  /deep/ .van-field__label {
    width: 100%;
  }

  /deep/ .van-field__body {
    padding-right: 8px;
    padding-left: 8px;
  }
}

.publish-field.substitute-control {
  display: flex;
  min-height: 42px;
  box-sizing: border-box;
  align-items: center;
  padding: 0 10px;
  background: @publish-surface;
  border: 1px solid @publish-border;
  border-radius: 6px;

  & + & {
    margin-top: 8px;
  }

  /deep/ .van-field__label {
    display: block;
    width: 42px;
    flex: 0 0 42px;
    align-self: center;
    margin: 0 8px 0 0;
    padding: 0;
    color: @publish-text-regular;
    font-size: 14px;
    font-weight: 400;
    line-height: 40px;
    text-align: left !important;
    text-align-last: left;
    letter-spacing: normal;
  }

  /deep/ .van-field__label::after {
    display: none !important;
    width: 0;
    height: 0;
    content: none !important;
  }

  /deep/ .van-field__value {
    width: auto;
    min-width: 0;
    flex: 1;
  }

  /deep/ .van-field__body {
    min-height: 40px;
    padding: 0;
    background: transparent;
    border: 0;
    border-radius: 0;
  }

  /deep/ .van-field__body:focus-within {
    border: 0;
  }

  /deep/ .van-field__control {
    text-align: left;
  }
}

.publish-field.textarea-field /deep/ .van-field__body {
  align-items: flex-start;
  min-height: 88px;
}

.publish-field {
  &--remark,
  &--remark-compact {
    /deep/ .van-field__value {
      position: relative;
    }

    /deep/ .van-field__body {
      min-height: 82px;
      align-items: flex-start;
      padding-bottom: 26px;
    }

    /deep/ .van-field__word-limit {
      position: absolute;
      right: 10px;
      bottom: 8px;
      margin: 0;
      color: @publish-text-secondary;
      font-size: 12px;
      line-height: 18px;
    }
  }

  &--remark-compact /deep/ .van-field__body {
    min-height: 74px;
  }

  &--summary {
    /deep/ .van-field__body {
      background: @publish-subtle-bg;
      border-color: @publish-border;
    }

    /deep/ .van-field__control {
      color: @publish-text-regular;
      font-weight: 500;
    }

    /deep/ .van-field__label {
      color: @publish-text-secondary;
      font-weight: 400;
    }
  }
}

.publish-field.validity-field /deep/ .van-field__right-icon {
  display: inline-flex;
  align-items: center;
  color: @publish-text-secondary;
  font-size: 18px;
}

.publish-field /deep/ .category-field-arrow {
  color: @publish-text-secondary;
  font-size: 16px;
}

.publish-field /deep/ .field-unit {
  color: @publish-text-secondary;
  font-size: 13px;
  white-space: nowrap;
}

.publish-field /deep/ .quantity-unit-control {
  display: flex;
  width: 100%;
  min-width: 0;
  align-items: center;
}

// Read-only backgrounds use the same surface as the summary field. Some
// native read-only controls remain selectable and opt out through the prop.
.publish-field.publish-field--readonly,
.publish-field.publish-field--disabled,
.publish-field.van-field--disabled {
  /deep/ .van-field__body {
    background: @publish-subtle-bg;
  }
}

.publish-field /deep/ .quantity-unit-control__quantity,
.publish-field /deep/ .quantity-unit-control__unit {
  height: 22px;
  min-width: 0;
  padding: 0;
  color: @publish-text-primary;
  font-size: 14px;
  line-height: 22px;
  background: transparent;
  border: 0;
  outline: 0;

  &::placeholder {
    color: @publish-placeholder;
  }
}

.publish-field /deep/ .quantity-unit-control__quantity {
  flex: 1 1 auto;
  padding-right: 10px;
}

.publish-field /deep/ .quantity-unit-control__unit {
  width: 72px;
  flex: 0 0 72px;
  padding-left: 10px;
  text-align: right;
  border-left: 0;
}
</style>
